import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, ChevronRight, Check } from "lucide-react";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  advertisement: boolean;
  functionality: boolean;
}

interface CookieConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CookieConsentModal({ open, onOpenChange }: CookieConsentModalProps) {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true - can't be disabled
    analytics: true,
    advertisement: true,
    functionality: true,
  });

  const [expandedSections, setExpandedSections] = useState({
    necessary: false,
    analytics: false,
    advertisement: false,
    functionality: false,
  });

  // Load saved preferences on mount
  useEffect(() => {
    const savedPrefs = localStorage.getItem('cookie-preferences');
    if (savedPrefs) {
      const parsed = JSON.parse(savedPrefs);
      setPreferences({ ...parsed, necessary: true }); // Ensure necessary is always true
    }
  }, []);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handlePreferenceChange = (type: keyof CookiePreferences, value: boolean) => {
    if (type === 'necessary') return; // Necessary cookies can't be disabled
    
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const savePreferences = () => {
    localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent-given', 'true');
    onOpenChange(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      advertisement: true,
      functionality: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookie-preferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookie-consent-given', 'true');
    onOpenChange(false);
  };

  const declineAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      advertisement: false,
      functionality: false,
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookie-preferences', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookie-consent-given', 'true');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        <div className="relative bg-white rounded-lg">
          {/* Header */}
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Manage cookies
            </DialogTitle>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 pb-6">
            {/* Description */}
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              This site uses cookies. We use cookies mainly to improve and analyze your experience on our 
              websites and for marketing purposes. Because we respect your right to privacy, you can 
              choose not to allow some types of cookies. Click on the different category headings to find 
              out more and change your default settings. Blocking some types of cookies may negatively 
              impact your experience on the site and limit the services we are able to provide.
            </p>

            {/* Cookie Categories */}
            <div className="space-y-4">
              {/* Necessary */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSection('necessary')}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    {expandedSections.necessary ? (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    )}
                    <span className="font-medium text-gray-900">Necessary</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Always active</span>
                  </div>
                </button>
                {expandedSections.necessary && (
                  <div className="px-4 pb-4 pt-0">
                    <p className="text-sm text-gray-600">
                      These cookies are necessary for the website to function and cannot be switched off in our systems. 
                      They are usually only set in response to actions made by you which amount to a request for services, 
                      such as setting your privacy preferences, logging in, or filling in forms. Learn more about necessary cookies.
                    </p>
                  </div>
                )}
              </div>

              {/* Analytics */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSection('analytics')}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    {expandedSections.analytics ? (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    )}
                    <span className="font-medium text-gray-900">Analytics</span>
                  </div>
                  <div className="flex items-center">
                    <div className="relative">
                      <Switch
                        checked={preferences.analytics}
                        onCheckedChange={(checked) => handlePreferenceChange('analytics', checked)}
                        className="data-[state=checked]:bg-teal-500"
                      />
                      {preferences.analytics && (
                        <Check className="absolute inset-0 m-auto h-3 w-3 text-white pointer-events-none" />
                      )}
                    </div>
                  </div>
                </button>
                {expandedSections.analytics && (
                  <div className="px-4 pb-4 pt-0">
                    <p className="text-sm text-gray-600">
                      These cookies help us to understand how visitors engage with the website. We may use a set of cookies 
                      to collect information and report site usage statistics. In addition to reporting site usage statistics, 
                      data collected may also be used, together with some of the advertising cookies described, to help show 
                      more relevant ads across the web and to measure interactions with the ads we show. Learn more about analytics cookies.
                    </p>
                  </div>
                )}
              </div>

              {/* Advertisement */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSection('advertisement')}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    {expandedSections.advertisement ? (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    )}
                    <span className="font-medium text-gray-900">Advertisement</span>
                  </div>
                  <div className="flex items-center">
                    <div className="relative">
                      <Switch
                        checked={preferences.advertisement}
                        onCheckedChange={(checked) => handlePreferenceChange('advertisement', checked)}
                        className="data-[state=checked]:bg-teal-500"
                      />
                      {preferences.advertisement && (
                        <Check className="absolute inset-0 m-auto h-3 w-3 text-white pointer-events-none" />
                      )}
                    </div>
                  </div>
                </button>
                {expandedSections.advertisement && (
                  <div className="px-4 pb-4 pt-0">
                    <p className="text-sm text-gray-600">
                      We use cookies to make our ads more engaging and valuable to site visitors. Some common applications 
                      of cookies are to select advertising based on what's relevant to a user; to improve reporting on ad 
                      campaign performance; and to avoid showing ads the user has already seen. Learn more about advertisement cookies.
                    </p>
                  </div>
                )}
              </div>

              {/* Functionality */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSection('functionality')}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    {expandedSections.functionality ? (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    )}
                    <span className="font-medium text-gray-900">Functionality</span>
                  </div>
                  <div className="flex items-center">
                    <div className="relative">
                      <Switch
                        checked={preferences.functionality}
                        onCheckedChange={(checked) => handlePreferenceChange('functionality', checked)}
                        className="data-[state=checked]:bg-teal-500"
                      />
                      {preferences.functionality && (
                        <Check className="absolute inset-0 m-auto h-3 w-3 text-white pointer-events-none" />
                      )}
                    </div>
                  </div>
                </button>
                {expandedSections.functionality && (
                  <div className="px-4 pb-4 pt-0">
                    <p className="text-sm text-gray-600">
                      We use a set of cookies that are optional for the website to function. They are usually only set in 
                      response to information provided to the website to personalize and optimize your experience as well as 
                      remember your chat history. Learn more about functionality cookies.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-8">
              <Button
                onClick={acceptAll}
                className="flex-1 bg-[#1a73e8] hover:bg-[#1557b0] text-white font-medium py-3"
              >
                Accept All
              </Button>
              <Button
                onClick={declineAll}
                variant="outline"
                className="flex-1 border-[#1a73e8] text-[#1a73e8] hover:bg-[#1a73e8] hover:text-white font-medium py-3"
              >
                Decline all
              </Button>
              <Button
                onClick={savePreferences}
                className="flex-1 bg-[#1a73e8] hover:bg-[#1557b0] text-white font-medium py-3"
              >
                Save settings
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}